"use client"

import * as React from "react"

import {
  getStorageItem,
  setStorageItem,
  subscribeToStorageKey,
} from "@exxatdesignux/ui/lib/persisted-state"

import { productPersistKey } from "@/stores/app-store"

export type EvaluationDashboardApproach =
  | "lifecycle"
  | "action"
  | "analytic"
  | "quality"
  | "operations"
  | "operations-v2"
  | "operations-v3"

export const EVALUATION_DASHBOARD_PRIMARY_APPROACHES: {
  id: EvaluationDashboardApproach
  label: string
  description: string
}[] = [
  {
    id: "lifecycle",
    label: "Lifecycle",
    description: "Current and last term cards, then past analysis",
  },
  {
    id: "action",
    label: "Action queue",
    description: "Prioritized work list with next steps",
  },
  {
    id: "analytic",
    label: "Analytics",
    description: "Closed term summary, trends, and exportable history",
  },
  {
    id: "quality",
    label: "Education quality",
    description: "Health score, learner voice, and where action is needed",
  },
]

export const EVALUATION_DASHBOARD_OPERATIONS_APPROACHES: {
  id: EvaluationDashboardApproach
  label: string
  description: string
}[] = [
  {
    id: "operations",
    label: "V1",
    description: "All active terms, collection stages, and coordinator actions",
  },
  {
    id: "operations-v2",
    label: "V2",
    description: "Needs-attention queue, live term, then compressed other terms",
  },
  {
    id: "operations-v3",
    label: "V3",
    description: "Operations KPIs with live and last closed terms side by side",
  },
]

export const EVALUATION_DASHBOARD_APPROACHES = [
  ...EVALUATION_DASHBOARD_PRIMARY_APPROACHES,
  ...EVALUATION_DASHBOARD_OPERATIONS_APPROACHES,
]

export function evaluationDashboardApproachMenuLabel(
  approach: EvaluationDashboardApproach,
): string {
  const operations = EVALUATION_DASHBOARD_OPERATIONS_APPROACHES.find(
    option => option.id === approach,
  )
  if (operations) return `Operation · ${operations.label}`

  return (
    EVALUATION_DASHBOARD_PRIMARY_APPROACHES.find(option => option.id === approach)?.label ??
    "Lifecycle"
  )
}

const STORAGE_KEY = productPersistKey("exxat-surveys", "dashboard-approach")
const VERSION = 3
const DEFAULT_APPROACH: EvaluationDashboardApproach = "operations"

const listeners = new Set<() => void>()
let snapshot: EvaluationDashboardApproach | null = null
let hydrated = false

function isApproach(value: unknown): value is EvaluationDashboardApproach {
  return (
    value === "lifecycle" ||
    value === "action" ||
    value === "analytic" ||
    value === "quality" ||
    value === "operations" ||
    value === "operations-v2" ||
    value === "operations-v3"
  )
}

function subscribe(onChange: () => void) {
  listeners.add(onChange)
  const unsubscribeStorage = subscribeToStorageKey(STORAGE_KEY, () => {
    hydrated = false
    onChange()
  })
  return () => {
    listeners.delete(onChange)
    unsubscribeStorage()
  }
}

function read(): EvaluationDashboardApproach {
  if (hydrated && snapshot) return snapshot
  hydrated = true

  const raw = getStorageItem(STORAGE_KEY)
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw)
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        (parsed as { v?: number }).v === VERSION &&
        isApproach((parsed as { d?: unknown }).d)
      ) {
        snapshot = (parsed as { d: EvaluationDashboardApproach }).d
        return snapshot
      }
    } catch {
      // fall through
    }
  }

  snapshot = DEFAULT_APPROACH
  return snapshot
}

export function setEvaluationDashboardApproach(next: EvaluationDashboardApproach) {
  snapshot = next
  hydrated = true
  setStorageItem(STORAGE_KEY, JSON.stringify({ v: VERSION, d: next }))
  for (const listener of listeners) listener()
}

export function useEvaluationDashboardApproach(): EvaluationDashboardApproach {
  return React.useSyncExternalStore(subscribe, read, () => DEFAULT_APPROACH)
}
